import { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { LoginPage } from "./auth/LoginPage";
import { RequireAuth } from "./auth/RequireAuth";
import { ChatBot } from "./components/ChatBot";
import { PaletteProvider } from "./components/PaletteProvider";
import { HomePage } from "./routes/Home";
import { InvitePage } from "./routes/Invite";
import { InvitesPage } from "./routes/InvitesPage";
import { EventDetailPage } from "./routes/EventDetailPage";
import { EventsPage } from "./routes/EventsPage";
import { GalleryPage } from "./routes/GalleryPage";
import { MembersPage } from "./routes/MembersPage";
import { NewsDetailPage } from "./routes/NewsDetailPage";
import { NewsPage } from "./routes/NewsPage";
import { PersonPage } from "./routes/PersonPage";
import { PostPage } from "./routes/PostPage";
import { SessionsPage } from "./routes/SessionsPage";
import { StatsPage } from "./routes/StatsPage";
import { TestingPage } from "./routes/TestingPage";
import { IssuesPage } from "./routes/IssuesPage";
import { HowToPage } from "./routes/HowToPage";
import { ReleaseNotesPage } from "./routes/ReleaseNotesPage";
import { SettingsPage } from "./routes/SettingsPage";
import { TreeAuditPage } from "./routes/TreeAuditPage";
import { ProfilePage } from "./routes/ProfilePage";
// Lazy-loaded: these two routes own the heaviest dependencies in the app —
// maplibre-gl (~1 MB of source) for the world map, and React Flow + dagre +
// d3 (~530 kB) for the tree graph. Statically imported they landed in the
// initial bundle for every visitor, including the majority who never open
// either page. Splitting them out cuts initial JS from 562 kB to 189 kB gzip.
const TreePage = lazy(() =>
  import("./routes/TreePage").then((m) => ({ default: m.TreePage })),
);
const WorldPage = lazy(() =>
  import("./routes/WorldPage").then((m) => ({ default: m.WorldPage })),
);
// The LiveKit SDK is large and only matters during a live get-together.
const EventLivePage = lazy(() =>
  import("./routes/EventLivePage").then((m) => ({ default: m.EventLivePage })),
);
import { AlbumsPage } from "./routes/AlbumsPage";
import { AlbumDetailPage } from "./routes/AlbumDetailPage";
import { ScanPage } from "./routes/ScanPage";
import { NotFoundPage } from "./routes/NotFound";

export function App() {
  return (
    <>
      {/* Covers the lazy routes below. Matches RequireAuth's loading state so
          a split-chunk fetch looks the same as the auth check that precedes it. */}
      <Suspense
        fallback={
          <div className="page">
            <p>Loading…</p>
          </div>
        }
      >
      <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />

      {/* Gated routes */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <HomePage />
          </RequireAuth>
        }
      />
      <Route
        path="/members"
        element={
          <RequireAuth>
            <MembersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/invites"
        element={
          <RequireAuth>
            <InvitesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        }
      />
      <Route
        path="/post/:postId"
        element={
          <RequireAuth>
            <PostPage />
          </RequireAuth>
        }
      />
      <Route
        path="/tree"
        element={
          <RequireAuth>
            <TreePage />
          </RequireAuth>
        }
      />
      <Route
        path="/tree-audit"
        element={
          <RequireAuth>
            <TreeAuditPage />
          </RequireAuth>
        }
      />
      <Route
        path="/person/:personId"
        element={
          <RequireAuth>
            <PersonPage />
          </RequireAuth>
        }
      />
      <Route
        path="/sessions"
        element={
          <RequireAuth>
            <SessionsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/stats"
        element={
          <RequireAuth>
            <StatsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/testing"
        element={
          <RequireAuth>
            <TestingPage />
          </RequireAuth>
        }
      />
      <Route
        path="/issues"
        element={
          <RequireAuth>
            <IssuesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/how-to"
        element={
          <RequireAuth>
            <HowToPage />
          </RequireAuth>
        }
      />
      <Route
        path="/release-notes"
        element={
          <RequireAuth>
            <ReleaseNotesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/events"
        element={
          <RequireAuth>
            <EventsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/events/:eventId"
        element={
          <RequireAuth>
            <EventDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/events/:eventId/live"
        element={
          <RequireAuth>
            <EventLivePage />
          </RequireAuth>
        }
      />
      <Route
        path="/news"
        element={
          <RequireAuth>
            <NewsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/news/:newsId"
        element={
          <RequireAuth>
            <NewsDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/gallery"
        element={
          <RequireAuth>
            <GalleryPage />
          </RequireAuth>
        }
      />
      <Route
        path="/world"
        element={
          <RequireAuth>
            <WorldPage />
          </RequireAuth>
        }
      />
      <Route
        path="/albums"
        element={
          <RequireAuth>
            <AlbumsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/albums/:albumId"
        element={
          <RequireAuth>
            <AlbumDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/scan"
        element={
          <RequireAuth>
            <ScanPage />
          </RequireAuth>
        }
      />

      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
    </Suspense>
    <ChatBot />
    <PaletteProvider />
    </>
  );
}
